import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X,
  PlayCircle,
  LayoutDashboard,
  FileText,
  Calculator,
  BookOpen,
  CreditCard,
  Settings,
  HelpCircle,
  Search,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { supabase } from "../Services/supabaseClient";
import "./HelpWalkthroughModal.css";

/**
 * Replace videoId with your real YouTube IDs.
 * Each video must have a stable unique id (string).
 */
const WALKTHROUGH_TABS = [
  {
    key: "overview",
    label: "Getting Started",
    icon: LayoutDashboard,
    videos: [
      {
        id: "overview-welcome",
        title: "Welcome to KostPadi",
        videoId: "6cKDKi7mACQ",
        desc: "High-level overview of KostPadi and how everything fits together.",
      },
      {
        id: "overview-dashboard",
        title: "Dashboard Tour",
        videoId: "6cKDKi7mACQ",
        desc: "Understand your dashboard metrics and shortcuts.",
      },
    ],
  },
  {
    key: "invoicing",
    label: "Invoicing",
    icon: FileText,
    videos: [
      {
        id: "inv-create",
        title: "Creating an Invoice",
        videoId: "6cKDKi7mACQ",
        desc: "Step-by-step guide to creating premium invoices.",
      },
      {
        id: "inv-pdf",
        title: "Invoice PDF & Branding",
        videoId: "6cKDKi7mACQ",
        desc: "Brand colors, logo, layout and PDF export best practices.",
      },
    ],
  },
  {
    key: "accounting",
    label: "Accounting",
    icon: BookOpen,
    videos: [
      {
        id: "acct-journals",
        title: "Journal Entries Explained",
        videoId: "6cKDKi7mACQ",
        desc: "Understand debits, credits, and journal-based accounting.",
      },
      {
        id: "acct-ledgers",
        title: "Ledgers & Trial Balance",
        videoId: "6cKDKi7mACQ",
        desc: "General Ledger, Account Ledger, and Trial Balance explained.",
      },
      {
        id: "acct-mgmt",
        title: "Management Reports",
        videoId: "6cKDKi7mACQ",
        desc: "How to read performance insights and make decisions.",
      },
    ],
  },
  {
    key: "costing",
    label: "Costing & Pricing",
    icon: Calculator,
    videos: [
      {
        id: "cost-estimation",
        title: "Cost Estimation",
        videoId: "6cKDKi7mACQ",
        desc: "How KostPadi helps you price correctly and profitably.",
      },
    ],
  },
  {
    key: "billing",
    label: "Billing & Plans",
    icon: CreditCard,
    videos: [
      {
        id: "billing-plans",
        title: "Subscription Plans",
        videoId: "6cKDKi7mACQ",
        desc: "Understanding billing plans and feature access.",
      },
    ],
  },
  {
    key: "settings",
    label: "Settings & Team",
    icon: Settings,
    videos: [
      {
        id: "settings-company",
        title: "Company & Profile Settings",
        videoId: "6cKDKi7mACQ",
        desc: "Configure your company info, branding, and preferences.",
      },
      {
        id: "settings-team",
        title: "Team & Roles",
        videoId: "6cKDKi7mACQ",
        desc: "Invite team members and manage permissions.",
      },
    ],
  },
];

// Route → tab mapping (auto-open based on current page)
const ROUTE_TAB_MAP = [
  { match: /^\/dashboard/i, tab: "overview" },
  { match: /^\/(invoice|total_invoice)/i, tab: "invoicing" },
  { match: /^\/(receipt|totalReceipts)/i, tab: "invoicing" },
  { match: /^\/estimate|total_estimate/i, tab: "costing" },
  {
    match:
      /^\/(journal|account-ledger|GeneralLedger|TrialBalance|income|sofp|cashFlows|managementReport|Reconcilliation)/i,
    tab: "accounting",
  },
  { match: /^\/billings/i, tab: "billing" },
  { match: /^\/(settings|team-settings)/i, tab: "settings" },
];

const allVideosFlat = () =>
  WALKTHROUGH_TABS.flatMap((t) =>
    t.videos.map((v) => ({
      ...v,
      tabKey: t.key,
      tabLabel: t.label,
    })),
  );

function getSuggestedTabFromPath(pathname) {
  const hit = ROUTE_TAB_MAP.find((r) => r.match.test(pathname || ""));
  return hit?.tab || "overview";
}

// Watched storage shape inside profiles.walkthrough_watched:
// { [videoId]: { watchedAt: isoString } }
function isWatched(watchedMap, videoId) {
  return !!watchedMap?.[videoId];
}

export default function HelpWalkthroughModal({
  open,
  onClose,
  pathname = "",
  defaultTab, // optional override
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [query, setQuery] = useState("");
  const [userId, setUserId] = useState(null);

  const [loadingProfile, setLoadingProfile] = useState(false);
  const [saving, setSaving] = useState(false);
  const [profileErr, setProfileErr] = useState("");

  const [watched, setWatched] = useState({}); // json map

  const searchRef = useRef(null);

  // Auto-select tab based on route when opened
  useEffect(() => {
    if (!open) return;
    const tab = defaultTab || getSuggestedTabFromPath(pathname);
    setActiveTab(tab);
    setQuery("");
    setTimeout(() => searchRef.current?.focus(), 50);
  }, [open, pathname, defaultTab]);

  // Fetch auth + profile
  useEffect(() => {
    const run = async () => {
      if (!open) return;
      setProfileErr("");
      setLoadingProfile(true);

      try {
        const {
          data: { user },
          error: uErr,
        } = await supabase.auth.getUser();

        if (uErr) throw uErr;
        if (!user?.id) {
          setUserId(null);
          setWatched({});
          return;
        }

        setUserId(user.id);

        const { data: profile, error: pErr } = await supabase
          .from("profiles")
          .select("id, has_seen_walkthrough, walkthrough_watched")
          .eq("id", user.id)
          .maybeSingle();

        if (pErr) throw pErr;

        // Mark seen if not seen (non-blocking)
        if (profile && profile.has_seen_walkthrough === false) {
          supabase
            .from("profiles")
            .update({ has_seen_walkthrough: true })
            .eq("id", user.id);
        }

        setWatched(profile?.walkthrough_watched || {});
      } catch (e) {
        console.error(e);
        setProfileErr("Could not load walkthrough progress.");
      } finally {
        setLoadingProfile(false);
      }
    };

    run();
  }, [open]);

  const tabs = useMemo(() => WALKTHROUGH_TABS, []);

  const currentTab = useMemo(
    () => tabs.find((t) => t.key === activeTab) || tabs[0],
    [tabs, activeTab],
  );

  // Global searchable list
  const allVideos = useMemo(() => allVideosFlat(), []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      return {
        mode: "tab",
        results: currentTab.videos.map((v) => ({
          ...v,
          tabKey: currentTab.key,
          tabLabel: currentTab.label,
        })),
      };
    }

    const results = allVideos.filter((v) => {
      const hay = `${v.title} ${v.desc} ${v.tabLabel}`.toLowerCase();
      return hay.includes(q);
    });

    return { mode: "search", results };
  }, [query, allVideos, currentTab]);

  // Recommended next video:
  // - If searching, recommend first unwatched in search results
  // - Otherwise recommend first unwatched in current tab; if none, global first unwatched.
  const recommended = useMemo(() => {
    const pickFirstUnwatched = (list) =>
      list.find((v) => !isWatched(watched, v.id)) || null;

    if (filtered.mode === "search") {
      return pickFirstUnwatched(filtered.results) || null;
    }

    const inTab = currentTab.videos.map((v) => ({
      ...v,
      tabKey: currentTab.key,
      tabLabel: currentTab.label,
    }));

    return pickFirstUnwatched(inTab) || pickFirstUnwatched(allVideos) || null;
  }, [filtered, watched, currentTab, allVideos]);

  // Mark as watched
  const markWatched = async (videoId) => {
    if (!userId) {
      // no auth; still mark locally for UX
      setWatched((w) => ({
        ...w,
        [videoId]: { watchedAt: new Date().toISOString() },
      }));
      return;
    }

    if (isWatched(watched, videoId)) return;

    const next = {
      ...(watched || {}),
      [videoId]: { watchedAt: new Date().toISOString() },
    };

    setWatched(next);

    try {
      setSaving(true);
      const { error } = await supabase
        .from("profiles")
        .update({ walkthrough_watched: next })
        .eq("id", userId);

      if (error) throw error;
    } catch (e) {
      console.error(e);
      // rollback not necessary; keep UX positive
    } finally {
      setSaving(false);
    }
  };

  // When a user plays a video, mark watched
  const onPlay = (videoId) => {
    markWatched(videoId);
  };

  // Jump to recommended’s tab
  const jumpToRecommended = () => {
    if (!recommended) return;
    setActiveTab(recommended.tabKey);
    setQuery("");
    // Scroll to top of list for clarity
    const el = document.querySelector(".kp-help-content");
    el?.scrollTo?.({ top: 0, behavior: "smooth" });
  };

  const close = () => onClose?.();

  if (!open) return null;

  return (
    <div className="kp-help-overlay" role="dialog" aria-modal="true">
      <div className="kp-help-modal">
        {/* Header */}
        <header className="kp-help-header">
          <div className="kp-help-title">
            <HelpCircle size={20} />
            <div className="kp-help-title-text">
              <h2>KostPadi Walkthrough</h2>
              <p className="kp-help-muted">
                Learn features fast — invoices, accounting, pricing, and more.
              </p>
            </div>
          </div>

          <button className="kp-help-close" onClick={close} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        {/* Search */}
        <div className="kp-help-searchRow">
          <div className="kp-help-search">
            <Search size={16} />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search videos… e.g. invoice, trial balance, journal"
            />
          </div>

          <div className="kp-help-progress">
            <span className="kp-help-muted">
              Watched:{" "}
              <strong>
                {Object.keys(watched || {}).length}/{allVideos.length}
              </strong>
            </span>
            {saving && <span className="kp-help-saving">Saving…</span>}
          </div>
        </div>

        {/* Body */}
        <div className="kp-help-body">
          {/* Sidebar Tabs */}
          <aside className="kp-help-tabs" aria-label="Walkthrough tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key && !query.trim();
              const watchedCount = tab.videos.filter((v) =>
                isWatched(watched, v.id),
              ).length;

              return (
                <button
                  key={tab.key}
                  className={`kp-help-tab ${isActive ? "active" : ""}`}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setQuery("");
                  }}
                >
                  <span className="kp-help-tabIcon">
                    <Icon size={16} />
                  </span>
                  <span className="kp-help-tabLabel">{tab.label}</span>
                  <span className="kp-help-tabCount">
                    {watchedCount}/{tab.videos.length}
                  </span>
                </button>
              );
            })}
          </aside>

          {/* Content */}
          <section className="kp-help-content">
            {profileErr && <div className="kp-help-alert">{profileErr}</div>}
            {loadingProfile && (
              <div className="kp-help-loading">Loading walkthrough…</div>
            )}

            {/* Recommended */}
            {recommended && (
              <div className="kp-help-reco">
                <div className="kp-help-recoLeft">
                  <div className="kp-help-recoBadge">
                    <Sparkles size={16} />
                    <span>Recommended next</span>
                  </div>
                  <div className="kp-help-recoTitle">
                    <strong>{recommended.title}</strong>
                    <span className="kp-help-muted">
                      {recommended.tabLabel}
                    </span>
                  </div>
                </div>
                <button className="kp-help-recoBtn" onClick={jumpToRecommended}>
                  Open
                </button>
              </div>
            )}

            {/* Heading */}
            <div className="kp-help-contentHead">
              <h3>
                {query.trim()
                  ? `Search results (${filtered.results.length})`
                  : currentTab.label}
              </h3>
              <p className="kp-help-sub">
                {query.trim()
                  ? "Showing matching walkthrough videos across KostPadi."
                  : "Watch the videos below to understand how this feature works."}
              </p>
            </div>

            {/* Videos */}
            <div className="kp-help-videos">
              {filtered.results.length === 0 ? (
                <div className="kp-help-empty">
                  No videos match “{query}”. Try a different keyword.
                </div>
              ) : (
                filtered.results.map((v) => {
                  const watchedThis = isWatched(watched, v.id);
                  return (
                    <div key={v.id} className="kp-video-card">
                      <div className="kp-video-frame">
                        <iframe
                          src={`https://www.youtube.com/embed/${v.videoId}`}
                          title={v.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          onLoad={() => onPlay(v.id)}
                        />
                      </div>

                      <div className="kp-video-meta">
                        <div className="kp-video-metaLeft">
                          <PlayCircle size={16} />
                          <div>
                            <strong>{v.title}</strong>
                            <p>{v.desc}</p>
                            {filtered.mode === "search" && (
                              <span className="kp-help-chip">{v.tabLabel}</span>
                            )}
                          </div>
                        </div>

                        <div className="kp-video-metaRight">
                          {watchedThis ? (
                            <span className="kp-watched">
                              <CheckCircle2 size={16} /> Watched
                            </span>
                          ) : (
                            <button
                              className="kp-markWatchedBtn"
                              onClick={() => markWatched(v.id)}
                            >
                              Mark watched
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="kp-help-footer">
          <p className="kp-help-muted">
            Tip: You can reopen this walkthrough anytime using the{" "}
            <strong>Help</strong> button inside the app.
          </p>
          <button className="kp-help-primary" onClick={close}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}
